import type { BaseEntity, SimpleTableProps } from "../types";
import type { SimpleTableSetupResult } from "../SimpleTableSetup";
import type { SimpleTableChromeValue } from "../contexts/SimpleTableContext";

/**
 * The CHROME slice of the SimpleTable context value — everything only the
 * header / body / pagination / bulk-action / modal sections read.
 *
 * These values churn (a fresh `savedViewsControls` node every parent render,
 * `isFetchingNextPage` flipping twice per infinite-scroll page, `paginatedData`
 * on every fetch) and NO row or cell reads them, so they are deliberately kept
 * out of the stable and volatile slices. That separation is what stops a
 * mid-scroll fetch from repainting every row.
 */

type ChromeProps<TData extends BaseEntity> = Partial<SimpleTableProps<TData>> &
  Pick<SimpleTableProps<TData>, "hooks">;

export function buildChromeSlice<TData extends BaseEntity>(
  setup: SimpleTableSetupResult<TData>,
  props: ChromeProps<TData>
): SimpleTableChromeValue<TData> {
  const { config, processedData, paginatedData, totalPages, openModal } = setup;

  return {
    processedData,
    paginatedData,
    totalPages,
    groupingContext: props.groupingContext,
    groupableFields: props.groupableFields,
    openModal,
    onCreate: props.onCreate,
    onBulkAction: props.onBulkAction,
    savedViewsControls: props.savedViewsControls,
    configSavedViews: config.savedViews,
    loadMore: props.loadMore,
    hasNextPage: props.hasNextPage,
    isFetchingNextPage: props.isFetchingNextPage,
    autoScrollEnabled: props.autoScrollEnabled,
    propTotalItems: props.totalItems,
    propTotalPages: props.totalPages,
    propOnPageChange: props.onPageChange,
    error: props.error,
    // Needed so the body can show a row SKELETON on first load instead of the
    // "No <entity> found" empty state, which read as "you have no pipeline"
    // while the query was still in flight.
    isLoading: props.isLoading,
    hideColumnHeaders: props.hideColumnHeaders,
    hideTableBody: props.hideTableBody,
  };
}
